import { useState } from 'react';
import { Helmet } from 'react-helmet';
import Layout from '@/components/Layout';
import { TourCard } from '@/components/TourCard';
import { TourFilters } from '@/components/TourFilters';
import { useTours } from '@/hooks/useTours';
import { Shield, Users, Star, Crown, ArrowRight } from 'lucide-react';
import toursHeroBg from '@/assets/tours-hero-bg.jpg';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import GoldenParticles from '@/components/GoldenParticles';
import WaveGoldDivider from '@/components/WaveGoldDivider';
import VIPBadge from '@/components/VIPBadge';
import AnimatedSection from '@/components/AnimatedSection';

const Tours = () => {
  const [filters, setFilters] = useState<any>({});
  const { tours, loading } = useTours();

  const handleFiltersChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  const filteredTours = tours.filter((tour) => {
    if (filters.category && tour.category_id !== filters.category) return false;
    if (filters.minPrice && tour.adult_price < filters.minPrice) return false;
    if (filters.maxPrice && tour.adult_price > filters.maxPrice) return false;
    return true;
  });

  const featuredTours = filteredTours.filter((tour) => tour.is_featured);

  return (
    <Layout>
      <Helmet>
        <title>VIP Tours & Excursions - Discover Dominican Republic | TROPUNCA VIP</title>
        <meta
          name="description"
          content="Explore our collection of exclusive VIP tours and excursions in Dominican Republic. From beach adventures to cultural experiences, find your perfect luxury tour."
        />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative min-h-[70vh] flex items-center justify-center bg-cover bg-center overflow-hidden"
        style={{ backgroundImage: `url(${toursHeroBg})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/70 to-primary/30" />
        <GoldenParticles count={20} />
        
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <AnimatedSection>
            <VIPBadge variant="exclusive" size="lg" icon="crown" className="mb-6" animated>
              Exclusive VIP Experiences
            </VIPBadge>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
              Discover <span className="text-gradient-gold">Amazing Tours</span>
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto mb-8 text-white/80">
              Experience the best of Dominican Republic with our carefully curated VIP tours and exclusive excursions
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="#tours">
                <Button size="lg" className="bg-gradient-gold text-white hover:opacity-90 shadow-gold text-lg px-8 group">
                  Browse VIP Tours
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-primary/50 text-white hover:bg-primary/20 text-lg px-8"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
        
        <WaveGoldDivider className="absolute bottom-0 left-0 right-0" />
      </section>

      {/* Features Strip */}
      <section className="bg-vip-dark text-white py-8 relative">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 bg-gradient-gold rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-white">Fully Insured</h3>
              <p className="text-sm text-white/70">Complete VIP safety coverage</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 bg-gradient-gold rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-white">Expert Guides</h3>
              <p className="text-sm text-white/70">Professional local specialists</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 bg-gradient-gold rounded-full flex items-center justify-center">
                <Star className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-white">5-Star VIP Service</h3>
              <p className="text-sm text-white/70">Exceptional exclusive experiences</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-background" id="tours">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Filters Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-24">
                <TourFilters onFiltersChange={handleFiltersChange} />
              </div>
            </aside>

            {/* Tours Grid */}
            <div className="lg:col-span-3">
              {loading ? (
                <div className="flex flex-col justify-center items-center min-h-[400px]">
                  <Crown className="w-12 h-12 animate-pulse-glow text-primary mb-4" />
                  <p className="text-muted-foreground">Loading VIP experiences...</p>
                </div>
              ) : (
                <>
                  {/* Featured Tours */}
                  {featuredTours.length > 0 && (
                    <AnimatedSection className="mb-12">
                      <div className="mb-6">
                        <VIPBadge variant="exclusive" className="mb-3">VIP Featured</VIPBadge>
                        <h2 className="text-3xl font-bold mb-2 text-gradient-gold">Featured VIP Tours</h2>
                        <p className="text-muted-foreground">
                          Our most popular and highly-rated exclusive experiences
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {featuredTours.map((tour, index) => (
                          <AnimatedSection key={tour.id} delay={index * 100}>
                            <TourCard tour={tour} />
                          </AnimatedSection>
                        ))}
                      </div>
                    </AnimatedSection>
                  )}

                  {/* All Tours */}
                  <AnimatedSection>
                    <div className="flex justify-between items-center mb-6">
                      <div>
                        <h2 className="text-3xl font-bold mb-2 text-gradient-gold">
                          {featuredTours.length > 0 ? 'All VIP Tours' : 'Available VIP Tours'}
                        </h2>
                        <p className="text-muted-foreground">
                          {filteredTours.length} {filteredTours.length === 1 ? 'tour' : 'tours'} available
                        </p>
                      </div>
                    </div>

                    {filteredTours.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {filteredTours.map((tour, index) => (
                          <AnimatedSection key={tour.id} delay={index * 50}>
                            <TourCard tour={tour} />
                          </AnimatedSection>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-xl text-muted-foreground">
                          No tours found matching your criteria
                        </p>
                      </div>
                    )}
                  </AnimatedSection>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-vip-dark relative overflow-hidden">
        <GoldenParticles count={20} />
        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection className="max-w-4xl mx-auto text-center">
            <Crown className="h-16 w-16 mx-auto mb-6 text-primary animate-float" />
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Ready for Your <span className="text-gradient-gold">VIP Adventure</span>?
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              Book your exclusive tour today and let us create an unforgettable VIP experience tailored to your preferences.
              All tours include professional guides, luxury transportation, and our commitment to excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-gradient-gold text-white hover:opacity-90 shadow-gold text-lg px-8 group">
                  Contact Our VIP Team
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/transfers">
                <Button variant="outline" size="lg" className="border-primary/50 text-white hover:bg-primary/20 text-lg px-8">
                  View VIP Transfers
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
};

export default Tours;
